import React from 'react';
import { v4 as uuid } from 'uuid';
import { addDays } from 'date-fns';
import { Page, useExecutiveCheckupRouter } from '../../router/context';
import SystemUnavailable from '../../../../components/SystemUnavailable';
import ModalTimeNotAvailable from '../../../../components/ModalTimeNotAvailable';
import { ProductSlotsTemplate } from '../../../../templates/ProductSlotsTemplate';
import { fetchDateSlots, fetchSlotsByDate } from '../../../../modules/slots';
import { useScheduleContext } from '../../../../contexts/ScheduleContext';
import { ScheduleItemPropsBuilder } from '../../../../models/Schedule';
import { DateSlots } from '../../../../templates/ProductSlotsTemplate/components/ProductSlots';
import { toast } from 'react-toastify';
import { useServiceContext } from '../../../../contexts/ServiceContext';
import { differenceInDays } from 'date-fns';
import { CoverageProps } from '../../../../models/Product';
import { useAttendanceUnitsWithDates } from '../../hooks/useAttendanceUnitsWithDates';

type ProductSlotsPageProps = {
  telemedicineCoverage?: CoverageProps;
};

export function ProductSlotsPage({ telemedicineCoverage }: ProductSlotsPageProps) {
  const { navigate, goBack } = useExecutiveCheckupRouter();
  const { selectedService: service } = useServiceContext();
  const { changeSchedule, selectedItem: schedule } = useScheduleContext();
  const [count, setCount] = React.useState(telemedicineCoverage ? 0 : 1);

  const [isModalSystemUnavailableVisible, setIsModalSystemUnavailableVisible] = React.useState(false);
  const [isModalSlotUnavailableVisible, setIsModalSlotUnavailableVisible] = React.useState(false);

  const { loading, unitDatesAvailable } = useAttendanceUnitsWithDates(telemedicineCoverage, schedule);

  const telemedicineDates = React.useMemo(() => {
    const unitDates = unitDatesAvailable.find(item => item.dates.length);

    if (unitDates && telemedicineCoverage?.units) {
      const unitAvailable = telemedicineCoverage?.units.find(unit => unit.id === unitDates.unitSghId);

      const scheduleFactory = ScheduleItemPropsBuilder();

      scheduleFactory.setChannel(telemedicineCoverage?.channel || '');
      scheduleFactory.setScheduleType({ careType: telemedicineCoverage.care_type });

      if (unitAvailable) {
        scheduleFactory.setUnit(unitAvailable, unitAvailable?.local_id || unitAvailable?.locations?.[0]?.name || '');
      }
      changeSchedule({ ...schedule, ...scheduleFactory.build() });

      return unitDates.dates.map(item => ({ id: uuid(), date: item } as DateSlots));
    }
    return [];
  }, [unitDatesAvailable]);

  const selectedProduct = React.useMemo(() => {
    return schedule && service.products.find(product => product.id === schedule.productId);
  }, [schedule]);

  const loadSlotsDates = async (baseDate: Date): Promise<DateSlots[]> => {
    if (count > 0) {
      const startDate = new Date();

      if (baseDate.getMonth() > startDate.getMonth() || baseDate.getFullYear() > startDate.getFullYear()) {
        startDate.setDate(1);
        startDate.setMonth(baseDate.getMonth());
        startDate.setFullYear(baseDate.getFullYear());
      }

      const endDate = addDays(startDate, 45);

      if (differenceInDays(endDate, startDate) < 4) endDate.setDate(endDate.getDate() + 5);

      try {
        const resourceId = schedule?.resourceId ?? '';

        const response = await fetchDateSlots({
          startDate: startDate.toISOString().substring(0, 10),
          endDate: endDate.toISOString().substring(0, 10),
          isKit: schedule?.isKit || false,
          itemId: schedule?.itemId || '',
          unitId: schedule?.unitId || '',
          localId: schedule?.localId || '',
          resourceId
        });

        return response.slotDates.map((item: string) => ({ id: uuid(), date: item } as DateSlots));
      } catch (error: any) {
        console.log('error', error);
        setIsModalSystemUnavailableVisible(true);
      }
    }
    setCount(value => value + 1);
    return [];
  };

  const loadSlotsHours = async (date: Date): Promise<DateSlots[]> => {
    try {
      if (schedule) {
        const resourceId = schedule.resourceId ?? '';

        const response = await fetchSlotsByDate({
          date: date.toISOString().substring(0, 10),
          isKit: schedule?.isKit || false,
          itemId: schedule?.itemId || '',
          unitId: schedule?.unitId || '',
          localId: schedule?.localId || '',
          resourceId
        });

        return response.map(item => ({ id: uuid(), date: `${item.date}T${item.hour}`, slot: item } as DateSlots));
      }
      return [];
    } catch (error) {
      console.error(error);
    }
    return [];
  };

  const submit = async (slotSelected: DateSlots) => {
    const scheduleFactory = ScheduleItemPropsBuilder();
    scheduleFactory.setTime({
      slots: [slotSelected.slot],
      time: new Date(slotSelected.date),
      ...slotSelected
    });

    changeSchedule({ ...schedule, ...scheduleFactory.setSlotsOptions([slotSelected.slot]) });

    navigate(Page.APPOINTMENT_CONFIRMATION);
  };

  return (
    <>
      <ProductSlotsTemplate
        headTitle="Escolha uma data e horário"
        goBack={goBack}
        productName={selectedProduct?.name ?? ''}
        productId={selectedProduct?.id ?? ''}
        handleSlot={submit}
        loadSlotsDates={loadSlotsDates}
        loadSlotsHours={loadSlotsHours}
        handleSelectSlot={_ => {}}
        customSlotsHead
        loading={loading}
        externalDisplayDates={telemedicineDates}
      />

      <SystemUnavailable
        visible={isModalSystemUnavailableVisible}
        onClose={() => setIsModalSystemUnavailableVisible(false)}
      />

      <ModalTimeNotAvailable
        visible={isModalSlotUnavailableVisible}
        onClose={() => setIsModalSlotUnavailableVisible(false)}
      />
    </>
  );
}
